/**
 * DeleteEdgeCommand - Thin command for deleting edge(s) (transitions) with undo/redo support
 *
 * Architecture:
 * - Command is THIN - only stores deleted transitions for undo/redo
 * - ALL business logic is in FlowCanvasCoordinator
 * - Coordinator handles: validation, ScreenTransitionManager, Canvas updates, Firebase sync
 *
 * Execution flow:
 * 1. Constructor reads transitions to be deleted from Coordinator (BEFORE execute)
 * 2. Command calls coordinator.deleteTransition() or coordinator.deleteTransitions()
 * 3. Coordinator does everything (struct update, Canvas, Firebase)
 *
 * Undo flow:
 * 1. Command calls coordinator.createTransition() for each deleted transition
 * 2. Coordinator does everything (struct update, Canvas, Firebase)
 * 3. TransitionIdGenerator creates same deterministic ID
 *
 * Sprint: sprint-2
 * Phase: 5 (Edge Commands Refactor - Thin Pattern)
 */

import type { CommandResult } from './Command';
import type { CommandContext } from './CommandContext';
import type { EntityTransition } from '@modules/reactFlow/types/EntityTransition';

const uniqueIds = (ids: string[]): string[] => Array.from(new Set(ids.filter(Boolean)));

/**
 * Command: Delete edge(s) (transitions) between screens
 *
 * This is a THIN command - all logic is delegated to Coordinator
 * Supports batch deletion of multiple edges at once
 */
export class DeleteEdgeCommand {
  public readonly id: string;
  public readonly description: string;
  public readonly timestamp: number;

  private readonly edgeIds: string[];
  private readonly context: CommandContext;

  // Deleted transitions (read in constructor BEFORE execute, used for undo)
  private readonly deletedTransitions: EntityTransition[];

  constructor(
    edgeIds: string[],
    context: CommandContext
  ) {
    this.id = `delete-edge-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    this.edgeIds = uniqueIds(edgeIds);
    this.context = context;
    this.timestamp = Date.now();
    this.description = `Delete ${this.edgeIds.length} edge(s)`;

    // Read transitions to be deleted BEFORE execute (for undo)
    const allTransitions = this.context.coordinator.getAllTransitions();
    this.deletedTransitions = allTransitions.filter(t => this.edgeIds.includes(t.id));

    console.log('[DeleteEdgeCommand] Constructor - stored transitions:', {
      count: this.deletedTransitions.length,
      ids: this.deletedTransitions.map(t => t.id.slice(0, 16))
    });
  }

  public canExecute(): boolean {
    // Can execute if at least one edge ID provided
    return this.edgeIds.length > 0;
  }

  public canUndo(): boolean {
    // Can undo if we have backup data
    return this.deletedTransitions.length > 0;
  }

  public async execute(): Promise<CommandResult> {
    if (!this.canExecute()) {
      return {
        success: false,
        error: new Error('No edge IDs provided for deletion'),
        affectedEntities: {}
      };
    }

    try {
      console.log('[DeleteEdgeCommand] execute:', {
        edgeCount: this.edgeIds.length,
        edgeIds: this.edgeIds.map(id => id.slice(0, 16))
      });

      // Coordinator does ALL the work:
      // 1. Finds owning screens for each transition
      // 2. Removes transitions from screen.struct via ScreenTransitionManager
      // 3. Updates ScreenRegistry
      // 4. Point update Canvas (removeEdges)
      // 5. Persists to Firebase
      // Use deleteTransitions for both single and batch operations
      await this.context.coordinator.deleteTransitions(this.edgeIds);

      console.log('[DeleteEdgeCommand]   ✓ Deleted');

      return {
        success: true,
        affectedEntities: {
          transitions: this.edgeIds
        }
      };
    } catch (error) {
      console.error('[DeleteEdgeCommand] ❌ Execution failed:', error);
      return {
        success: false,
        error: error as Error,
        affectedEntities: {}
      };
    }
  }

  public async undo(): Promise<CommandResult> {
    if (!this.canUndo()) {
      return {
        success: false,
        error: new Error('Cannot undo: no transitions were deleted'),
        affectedEntities: {}
      };
    }

    try {
      console.log('[DeleteEdgeCommand] undo:', {
        count: this.deletedTransitions.length,
        edgeIds: this.deletedTransitions.map(t => t.id.slice(0, 16))
      });

      // Coordinator does ALL the work for each transition:
      // 1. Creates transition object with deterministic ID
      // 2. Updates screen.struct via ScreenTransitionManager
      // 3. Updates ScreenRegistry
      // 4. Point update Canvas (addEdge)
      // 5. Persists to Firebase
      // IMPORTANT: TransitionIdGenerator will create THE SAME ID
      for (const transition of this.deletedTransitions) {
        await this.context.coordinator.createTransition({
          sourceEntityId: transition.sourceEntityId,
          targetEntityId: transition.targetEntityId,
          sourceActionKey: transition.sourceActionKey,
          label: transition.label,
        });
      }

      console.log('[DeleteEdgeCommand]   ✓ Undone');

      return {
        success: true,
        affectedEntities: {
          transitions: this.deletedTransitions.map(t => t.id)
        }
      };
    } catch (error) {
      console.error('[DeleteEdgeCommand] ❌ Undo failed:', error);
      return {
        success: false,
        error: error as Error,
        affectedEntities: {}
      };
    }
  }

  public async redo(): Promise<CommandResult> {
    // Re-execute deletion
    return this.execute();
  }
}
